import { useEffect, useState } from "react";
import { Lock, LockOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { eur } from "../../lib/format";
import { MONTHS_NL } from "../../lib/format";
import api from "../../lib/api";
import type { MonthSummary } from "../../types/bank";

const checkItems = [
  "Alle banktransacties van de maand zijn gekoppeld of genegeerd (bankreconciliatie).",
  "Afwijkingen zijn opgelost of bewust geëscaleerd (tabblad Controle).",
  "Daarna kun je de periode afsluiten: boekingen in afgesloten maanden worden geblokkeerd.",
];

/** Maandafsluiting: jaartabel per maand met statussen + sluiten/heropenen. */
export function MonthClosePanel({ onChanged }: { onChanged?: () => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [rows, setRows] = useState<MonthSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get("/api/bank-transactions/summary", { params: { year } })
      .then((r) => setRows(r.data ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [year]);

  const closeMonth = async (m: MonthSummary) => {
    const pending = m.unmatched + m.suggested;
    let force = false;
    if (pending > 0) {
      const ok = confirm(
        `Maand ${MONTHS_NL[m.month - 1]} heeft ${pending} niet-gekoppelde transactie(s).\n\nAfsluiten wordt aanbevolen pas als alles gekoppeld is. Toch geforceerd afsluiten?`,
      );
      if (!ok) return;
      force = true;
    }
    setBusy(`${m.month}`);
    try {
      await api.post("/api/bank-transactions/locks/close", { year, month: m.month, force });
      load();
      onChanged?.();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Afsluiten mislukt.");
    } finally {
      setBusy(null);
    }
  };

  const reopenMonth = async (m: MonthSummary) => {
    if (!confirm(`Maand ${MONTHS_NL[m.month - 1]} ${year} weer openen? Boekingen zijn dan weer mogelijk.`)) return;
    setBusy(`${m.month}`);
    try {
      // Heropenen = de lock-rij verwijderen; we zoeken hem via de locks-lijst.
      const { data: locks } = await api.get("/api/bank-transactions/locks");
      const lock = (locks ?? []).find((l: any) => l.year === year && l.month === m.month);
      if (lock) await api.delete(`/api/bank-transactions/locks/${lock.id}`);
      load();
      onChanged?.();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Heropenen mislukt.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-3">
        {checkItems.map((c, i) => (
          <div key={i} className="surface-card p-4 flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
            <p className="text-sm text-slate-600">{c}</p>
          </div>
        ))}
      </div>

      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Maandafsluiting {year}</h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setYear((y) => y - 1)}><ChevronLeft size={16} /></Button>
            <span className="font-semibold text-slate-700">{year}</span>
            <Button variant="ghost" size="sm" onClick={() => setYear((y) => y + 1)}><ChevronRight size={16} /></Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                <th className="py-2 pr-3">Maand</th>
                <th className="py-2 pr-3 text-right">In</th>
                <th className="py-2 pr-3 text-right">Uit</th>
                <th className="py-2 pr-3 text-right">Niet gekoppeld</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 text-right">Actie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => {
                const pending = m.unmatched + m.suggested;
                const isFuture = new Date(Date.UTC(year, m.month, 1)) > new Date();
                return (
                  <tr key={m.month} className={m.locked ? "bg-slate-50/60" : ""}>
                    <td className="py-2.5 pr-3 font-semibold text-slate-700">{MONTHS_NL[m.month - 1]}</td>
                    <td className="py-2.5 pr-3 text-right text-emerald-600">{eur(m.inEur)}</td>
                    <td className="py-2.5 pr-3 text-right text-rose-600">{eur(m.uitEur)}</td>
                    <td className="py-2.5 pr-3 text-right">
                      {pending > 0 ? <span className="text-amber-600 font-semibold">{pending}</span> : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      {m.locked ? (
                        <Badge className="bg-slate-800 text-white border-slate-800"><Lock size={12} className="inline -mt-0.5 mr-1" /> Afgesloten</Badge>
                      ) : isFuture ? (
                        <Badge className="bg-slate-50 text-slate-400 border-slate-200">—</Badge>
                      ) : pending > 0 ? (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200">Open · actie nodig</Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Open · compleet</Badge>
                      )}
                    </td>
                    <td className="py-2.5 text-right">
                      {m.locked ? (
                        <Button variant="ghost" size="sm" onClick={() => reopenMonth(m)} loading={busy === String(m.month)}>
                          <LockOpen size={16} /> Heropenen
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => closeMonth(m)} loading={busy === String(m.month)} disabled={isFuture}>
                          <Lock size={16} /> Sluiten
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={6} className="py-4 text-center text-slate-400">Geen gegevens voor {year}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}